/**
 * Yandex Metrica tag, plus conversion events for GA4 and Metrica.
 *
 * Yandex was ~70% of the site's traffic before April 2026 and is still the largest search
 * source, yet the site had no Metrica tag: Webmaster could not tie queries to visits, and
 * there was no behavioural data from Yandex's side at all. 0 disables the tag.
 */
export const YANDEX_METRICA_ID = 114094883;

type YmFn = ((id: number, method: string, ...args: unknown[]) => void) & { a?: unknown[][]; l?: number };
type AnalyticsWindow = Window & { ym?: YmFn; gtag?: (...args: unknown[]) => void };

export function initYandexMetrica() {
    if (!YANDEX_METRICA_ID || typeof window === "undefined") return;
    const w = window as AnalyticsWindow;
    if (w.ym) return;
    // The vendor snippet, minus document.write: a queueing stub until tag.js arrives.
    const ym: YmFn = (...args: unknown[]) => {
        (ym.a = ym.a || []).push(args);
    };
    ym.l = Date.now();
    w.ym = ym;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://mc.yandex.ru/metrika/tag.js?id=${YANDEX_METRICA_ID}`;
    document.head.appendChild(s);
    w.ym(YANDEX_METRICA_ID, "init", {
        // Route changes are reported by yandexMetricaHit, like the GA4 pageviews.
        defer: false,
        referrer: document.referrer,
        url: window.location.href,
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
        webvisor: true,
    });
}

/** SPA navigation: Metrica's init counts the landing page itself, later routes need a hit. */
export function yandexMetricaHit(path: string, previousPath: string | null) {
    const w = window as AnalyticsWindow;
    if (!YANDEX_METRICA_ID || !w.ym) return;
    const origin = window.location.origin;
    w.ym(YANDEX_METRICA_ID, "hit", `${origin}${path}`, {
        referer: previousPath ? `${origin}${previousPath}` : document.referrer,
    });
}

/**
 * A conversion: a GA4 event and a Metrica goal of the same name. Copying an import code
 * is what visitors come for, and until now neither tool recorded it — every report
 * could count sessions but not whether anyone left with a crosshair.
 * Both tags load on idle, so an event fired before then is dropped (GA) or queued (Metrica).
 */
export function trackEvent(name: string, params: Record<string, string | number> = {}) {
    if (typeof window === "undefined") return;
    const w = window as AnalyticsWindow;
    try {
        w.gtag?.("event", name, params);
    } catch {
        /* analytics must never break the page */
    }
    try {
        if (YANDEX_METRICA_ID && w.ym) w.ym(YANDEX_METRICA_ID, "reachGoal", name, params);
    } catch {
        /* analytics must never break the page */
    }
}
